import * as Enumerable from 'linq';
import { BulkActionDescriptor, BulkActionDescriptorInput, BulkActionOperation } from '../../../../entities/bulk.actions/bulk.action.descriptor';
import { CustomFieldUtility } from '../../../../entities/fields/custom.field';
import { CustomFieldType } from '../../../../entities/fields/custom.field.type';
import { MetadataEntityData } from '../model/metadata.entity.data';
import { MetadataEntityFieldData } from '../model/metadata.entity.field.data';
import { MetadataEntityFieldType } from '../model/metadata.entity.field.types';
import { MetadataEntityRecordScope } from '../model/metadata.entity.record.scope';
import { getMetadataEntityFieldType } from '../utilities/get.entity.field.type';
import { MetadataEmbeddedLevel, resolveEmbeddedLevels, scopeForEmbeddedLevel } from '../utilities/resolve.embedded.levels';

/**
 * Field types where bulk-updating across many records is a coherent action (idempotent set,
 * picker-driven values, or limited domain). Other types (free-text strings, numbers, files,
 * embedded entities, calculated, etc.) are excluded because bulk-applying the same value
 * across records is either dangerous or unrepresentable in the UI.
 *
 * CustomFieldDropdown is included because custom-field dropdowns behave like an enum picker;
 * the canonical CustomFieldType → MetadataEntityFieldType map in CustomFieldUtility maps both
 * Dropdown and DropdownMulti to CustomFieldDropdown.
 */
const BULK_UPDATABLE_FIELD_TYPES: ReadonlySet<MetadataEntityFieldType> = new Set([
    MetadataEntityFieldType.Lookup,
    MetadataEntityFieldType.Tag,
    MetadataEntityFieldType.Enum,
    MetadataEntityFieldType.EntityStatus,
    MetadataEntityFieldType.Flag,
    MetadataEntityFieldType.Boolean,
    MetadataEntityFieldType.Date,
    MetadataEntityFieldType.DateTime,
    MetadataEntityFieldType.CustomFieldDropdown,
]);

export interface MetadataCustomFieldCapability {
    filterable: boolean;
    groupable: boolean;
    textFilterable?: boolean;
}

const METADATA_CUSTOM_FIELD_CAPABILITY: Partial<Record<CustomFieldType, MetadataCustomFieldCapability>> = {
    [CustomFieldType.Dropdown]: { filterable: true, groupable: false },
    [CustomFieldType.DropdownMulti]: { filterable: true, groupable: false },
    [CustomFieldType.Boolean]: { filterable: true, groupable: false },
    [CustomFieldType.Number]: { filterable: true, groupable: false },
    [CustomFieldType.Text]: { filterable: true, groupable: false, textFilterable: true },
    [CustomFieldType.Email]: { filterable: true, groupable: false, textFilterable: true },
    [CustomFieldType.Phone]: { filterable: true, groupable: false, textFilterable: true },
    [CustomFieldType.Date]: { filterable: true, groupable: false },
    [CustomFieldType.DateTime]: { filterable: true, groupable: false },
};

export function getMetadataCustomFieldCapability(type: CustomFieldType): MetadataCustomFieldCapability {

    return METADATA_CUSTOM_FIELD_CAPABILITY[type] ?? { filterable: false, groupable: false };
}

function isFieldTypeBulkUpdatable(field: MetadataEntityFieldData): boolean {

    const rawType = Array.isArray(field.type) ? field.type[0] : field.type;

    // Custom fields delegate to their canonical resolved type so an email/text custom field
    // is excluded but a dropdown custom field is eligible.
    if (rawType === MetadataEntityFieldType.CustomField) {
        const resolved = CustomFieldUtility.getMetadataFieldType(field.name);
        return BULK_UPDATABLE_FIELD_TYPES.has(resolved);
    }

    return BULK_UPDATABLE_FIELD_TYPES.has(rawType) && field.bulkUpdatable === true;
}

/**
 * A field qualifies for bulk update when:
 *  - Its type is in the eligible set (or its CustomField inner kind is). Ineligible types are
 *    excluded — the UI has no editor for them and bulk-applying a free-text/number/file value
 *    is dangerous.
 *  - The field isn't hidden or readonly.
 *
 * Eligibility is inferred from field type — the per-field `bulkUpdatable` flag is no longer
 * consulted because the default value (`false` from getMetadataEntityFieldDataDefaults) would
 * suppress every field. If an entity needs to exclude a specific eligible field from bulk
 * updates, use `bulkUpdateOverrides: { hidden: true }` or rename/remove the field.
 */
function isBulkUpdateEligible(field: MetadataEntityFieldData): boolean {

    if (field.hidden || field.readonly) return false;
    return isFieldTypeBulkUpdatable(field);
}

interface ParentField {
    path: string;
    field: MetadataEntityFieldData;
}

export interface FieldWithValue extends ParentField {
    value: any;
}

export class MetadataEntityFieldDomain {

    public get name() {
        return this.metadata.name;
    }

    public get displayName() {
        return this.metadata.displayName;
    }

    public get type() {
        return this.metadata.type;
    }

    public get module() {
        return this.metadata.module;
    }

    public get icon() {
        return this.metadata.icon;
    }

    public get visualType() {
        return this.metadata.visualType;
    }

    public readonly fields: Record<string, MetadataEntityFieldData> = {};
    public readonly uiFields: Record<string, MetadataEntityFieldData> = {};
    public readonly bulkUpdateFields: Record<string, MetadataEntityFieldData> = {};
    public readonly customFields: Record<string, MetadataEntityFieldData> = {};
    public readonly propagateChangeFields: Record<string, MetadataEntityFieldData> = {};
    public readonly bulkActions: BulkActionDescriptor[] = [];

    /**
     * Builds flattened metadata field domains.
     * @param metadata Root metadata to flatten.
     * @param options.includeEmbeddedLevels When false, includes only fields owned by metadata;
     * otherwise absolute embedded fields are merged into uiFields and bulkUpdateFields only.
     */
    constructor(
        public readonly metadata: MetadataEntityData,
        options?: { includeEmbeddedLevels?: boolean },
    ) {

        if (!metadata?.fields) return;

        const includeEmbeddedLevels = options?.includeEmbeddedLevels !== false;
        const primaryScope = this.resolvePrimaryScope();

        this.fields = this.getFields('default', {}, null, metadata.fields, primaryScope, metadata.displayName);
        this.uiFields = this.getFields('ui', {}, null, metadata.fields, primaryScope, metadata.displayName);
        this.bulkUpdateFields = this.getFields('bulkUpdate', {}, null, metadata.fields, primaryScope, metadata.displayName);
        this.customFields = this.getFields('customFields', {}, null, metadata.fields, primaryScope, metadata.displayName);
        this.propagateChangeFields = this.getFields('propagateChange', {}, null, metadata.fields, primaryScope, metadata.displayName);

        // Walk entity-owned secondaryFilter chain (Job → Stage → Code) and merge each level's own fields
        const embeddedLevels = includeEmbeddedLevels ? resolveEmbeddedLevels(metadata) : [];
        for (const level of embeddedLevels) {
            const levelDomain = new MetadataEntityFieldDomain(level.metadata, { includeEmbeddedLevels: false });
            const scope = scopeForEmbeddedLevel(level);
            this.mergeSecondaryFields(this.uiFields, levelDomain.uiFields, level.absolutePath, level.metadata.displayName, scope);
            this.mergeSecondaryFields(this.bulkUpdateFields, levelDomain.bulkUpdateFields, level.absolutePath, level.metadata.displayName, scope);
        }

        this.bulkActions = this.buildBulkActions(embeddedLevels);
    }

    /**
     * Returns fields filtered by record grain.
     * An entity with no `secondaryFilter` has all fields stamped as `Any`, so any scope query returns everything.
     * `Any` fields always match regardless of the queried scope.
     * Path grains (e.g. `workOrders.codes`) match fields stamped with that path.
     */
    public fieldsByScope(scope: MetadataEntityRecordScope | string, source: 'ui' | 'bulkUpdate' | 'default' = 'ui'): Record<string, MetadataEntityFieldData> {

        const map = source === 'bulkUpdate'
            ? this.bulkUpdateFields
            : source === 'default'
                ? this.fields
                : this.uiFields;

        if (scope === MetadataEntityRecordScope.Any) return map;

        const result: Record<string, MetadataEntityFieldData> = {};
        for (const [path, field] of Object.entries(map)) {
            const fieldScope = field.scope ?? MetadataEntityRecordScope.Any;
            if (fieldScope === MetadataEntityRecordScope.Any || fieldScope === scope) {
                result[path] = field;
            }
        }
        return result;
    }

    private resolvePrimaryScope(): MetadataEntityRecordScope {

        return this.metadata.secondaryFilter?.arrayPath
            ? MetadataEntityRecordScope.Primary
            : MetadataEntityRecordScope.Any;
    }

    private buildBulkActions(embeddedLevels: MetadataEmbeddedLevel[]): BulkActionDescriptor[] {

        // Bulk updates are opt-in per entity. If the entity didn't opt in, emit nothing
        // even if individual fields declare `bulkUpdatable: true`.
        if (!this.metadata.bulkUpdates) return [];

        const fieldUpdates = Enumerable
            .from(Object.values(this.bulkUpdateFields))
            .selectMany(field => this.toFieldUpdateDescriptors(field))
            .toArray();

        const commands = [
            ...(this.metadata.bulkActions ?? []).map(input => this.toCommandDescriptor(input)),
            ...embeddedLevels.flatMap(level => this.toEmbeddedCommandDescriptors(level)),
        ];

        // Sort each segment alphabetically by label, but keep the segment order
        // (primary → first secondary → deeper embedded → commands) so visual grouping
        // by entity grain is preserved.
        const isPrimary = (d: BulkActionDescriptor) =>
            d.scope === MetadataEntityRecordScope.Primary || d.scope === MetadataEntityRecordScope.Any || !d.scope;
        const isFirstSecondary = (d: BulkActionDescriptor) => d.scope === MetadataEntityRecordScope.Secondary;
        const primaryFieldUpdates = fieldUpdates.filter(isPrimary).sort((a, b) => a.label.localeCompare(b.label));
        const secondaryFieldUpdates = fieldUpdates.filter(isFirstSecondary).sort((a, b) => a.label.localeCompare(b.label));
        const deeperFieldUpdates = fieldUpdates
            .filter(d => !isPrimary(d) && !isFirstSecondary(d))
            .sort((a, b) => a.label.localeCompare(b.label));
        const sortedCommands = [...commands].sort((a, b) => a.label.localeCompare(b.label));

        return [...primaryFieldUpdates, ...secondaryFieldUpdates, ...deeperFieldUpdates, ...sortedCommands];
    }

    private toFieldUpdateDescriptors(field: MetadataEntityFieldData): BulkActionDescriptor[] {

        const fieldType = Array.isArray(field.type) ? field.type[0] : field.type;

        // Multi-value tags decompose into Add + Remove. A scalar tag is a single-value field and uses Set.
        if (fieldType === MetadataEntityFieldType.Tag && Array.isArray(field.type)) {
            return [
                this.buildFieldUpdateDescriptor(field, 'add'),
                this.buildFieldUpdateDescriptor(field, 'remove'),
            ];
        }

        return [this.buildFieldUpdateDescriptor(field, 'set')];
    }

    private buildFieldUpdateDescriptor(field: MetadataEntityFieldData, operation: BulkActionOperation): BulkActionDescriptor {

        const fieldId = field.path ?? field.name;

        return {
            id: this.buildFieldUpdateActionId(fieldId, operation),
            label: this.buildFieldUpdateLabel(field, operation),
            kind: 'field-update',
            scope: field.scope ?? MetadataEntityRecordScope.Any,
            field: fieldId,
            operation,
            requiredRights: this.metadata.bulkUpdateRequiredRights,
            editor: field.bulkUpdateEditor ?? {
                fields: [{
                    id: 'value',
                    type: field.type,
                    typeData: field.typeData,
                    editor: field.editor,
                    displayName: field.displayName,
                    required: field.required,
                }]
            }
        };
    }

    private buildFieldUpdateActionId(fieldId: string, operation: BulkActionOperation): string {

        const prefixByOperation: Record<BulkActionOperation, string> = {
            set: 'update',
            add: 'add',
            remove: 'remove',
        };
        return `${prefixByOperation[operation]}.${fieldId}`;
    }

    /**
     * Sentence-cased, verb-by-type labels modeled on Linear's command palette:
     *   - Lookup / Tag / Enum / Status → "Change status…" / "Assign to…" / "Add labels…"
     *   - Date → "Set due date…"
     *   - Boolean → "Set billed…"
     * Trailing ellipsis (…) signals that the action navigates to a follow-up picker
     * rather than firing immediately.
     */
    private buildFieldUpdateLabel(field: MetadataEntityFieldData, operation: BulkActionOperation): string {

        const subject = this.toSentenceCaseSubject(this.resolveBulkActionSubject(field));
        const verb = this.resolveFieldUpdateVerb(field, operation);
        return `${verb} ${subject}…`;
    }

    private resolveFieldUpdateVerb(field: MetadataEntityFieldData, operation: BulkActionOperation): string {

        if (operation === 'add') return 'Add';
        if (operation === 'remove') return 'Remove';

        const fieldType = Array.isArray(field.type) ? field.type[0] : field.type;
        switch (fieldType) {
            case MetadataEntityFieldType.Date:
            case MetadataEntityFieldType.DateTime:
            case MetadataEntityFieldType.Boolean:
            case MetadataEntityFieldType.Flag:
                return 'Set';
            default:
                return 'Change';
        }
    }

    private toSentenceCaseSubject(subject: string): string {

        // Keep the first character as authored (handles acronyms like "ID")
        // but lowercase subsequent words so the label reads as a sentence.
        return subject
            .split(' ')
            .map((word, index) => (index === 0 ? word : word.toLowerCase()))
            .join(' ')
            .replace(/^(.)/, m => m.toLowerCase());
    }

    /**
     * Builds the human-readable subject used in bulk-action labels.
     *
     * - Primary/Any scope: just the field display name (group header carries the entity context).
     *     "Status" → "Update Status", "Tags" → "Add Tags"
     * - Secondary scope: prefix with the secondary entity's display name so the row is unambiguous when
     *   sitting alongside primary fields of the same name.
     *     "Stage" + "Status" → "Update Stage Status"
     *     "Stage" + "Stage Owner" → "Update Stage Owner" (don't double-prefix if field already starts with the entity)
     */
    private resolveBulkActionSubject(field: MetadataEntityFieldData): string {

        const fieldName = field.displayName ?? field.name;

        // Prefix bulk labels for any non-primary embedded grain (first secondary or path grain)
        if (field.scope === MetadataEntityRecordScope.Primary || field.scope === MetadataEntityRecordScope.Any || !field.scope) {
            return fieldName;
        }

        const secondaryName = field.group;
        if (!secondaryName) return fieldName;
        if (fieldName.toLowerCase().startsWith(secondaryName.toLowerCase())) return fieldName;

        return `${secondaryName} ${fieldName}`;
    }

    private toCommandDescriptor(input: BulkActionDescriptorInput): BulkActionDescriptor {

        return {
            ...input,
            kind: 'command',
            scope: input.scope ?? MetadataEntityRecordScope.Any,
        };
    }

    /**
     * Commands declared on an embedded entity (e.g. Stage's `claim.process`) are actions on that
     * grain, so they only reach a board through the root entity that owns the chain — a board is
     * always mounted on the root and never on the embedded metadata itself.
     *
     * The id is prefixed with the level's absolute path, mirroring how field-update ids are already
     * namespaced (`update.workOrders.status`). That keeps an embedded command from colliding with a
     * same-named root command when the view grain is `Any`, and callers strip the prefix to recover
     * the id as declared on the embedded entity. The level's own scope stamp wins over anything
     * authored on the descriptor — the grain is a property of where the command lives in the chain.
     */
    private toEmbeddedCommandDescriptors(level: MetadataEmbeddedLevel): BulkActionDescriptor[] {

        // Bulk is opt-in per entity, same as the root gate above.
        if (!level.metadata.bulkUpdates) return [];

        return (level.metadata.bulkActions ?? []).map(input => ({
            ...this.toCommandDescriptor(input),
            id: `${level.absolutePath}.${input.id}`,
            scope: scopeForEmbeddedLevel(level),
        }));
    }

    /**
     * Retrieves the values of metadata fields from an item based on their paths.
     *
     * @param metadataFields A record of metadata fields to retrieve values from.
     * @param item The item from which to extract the values.
     * @returns An array of objects, each containing the field path and its corresponding value.
     */
    public getValuesByPath(metadataFields: Record<string, MetadataEntityFieldData>, item: any): FieldWithValue[] {

        const result = Enumerable
            .from(Object.keys(metadataFields))
            .select(path => {
                return {
                    path,
                    field: metadataFields[path],
                    value: this.getValueByPath(item, path)
                };
            })
            .where(x => x.value !== undefined)
            .toArray();

        return result;

    }

    /**
     * Retrieves a value from a nested object using a specified path.
     *
     * @param obj The object from which to retrieve the value.
     * @param path The path to the desired value (e.g., 'parent.child.property').
     * @returns The value found at the specified path, or undefined if the path does not exist.
     */
    private getValueByPath(obj: any, path: string): any {
        if (!obj || !path) return undefined;

        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }

    /**
     * Recursively processes and flattens the entity fields based on the specified type.
     *
     * @param getType The type of fields to retrieve ('default', 'ui', 'bulkUpdate', 'customFields', 'propagateChange').
     * @param fieldMap The map to store the flattened fields.
     * @param parent The parent field, if any.
     * @param fields The array of fields to process.
     * @returns A record of the flattened fields.
     */
    private getFields(getType: 'default' | 'ui' | 'bulkUpdate' | 'customFields' | 'propagateChange', fieldMap: Record<string, MetadataEntityFieldData>, parent: ParentField | null, fields: MetadataEntityFieldData[], scope: MetadataEntityRecordScope, groupName?: string, pathPrefix?: string) {

        if (!fields) return fieldMap;

        for (const field of fields) {

            let safeField = { ...field };

            switch (getType) {
                case 'bulkUpdate': {
                    const uiOverride = field.uiOverrides ?? {};
                    const bulkOverride = field.bulkUpdateOverrides ?? {};
                    safeField = { ...field, ...uiOverride, ...bulkOverride };
                    break;
                }

                case 'customFields':
                case 'ui': {
                    const uiOverride = field.uiOverrides ?? {};
                    safeField = { ...field, ...uiOverride };
                    break;
                }

                case 'propagateChange': {
                    if (field.propagateChange !== true) {
                        continue;
                    }
                    break;
                }


            }

            let path = parent?.path
                ? `${parent?.path}.${safeField.name}`
                : pathPrefix ? `${pathPrefix}.${safeField.name}` : safeField.name;

            let name = parent?.path
                ? safeField.displayName
                : safeField.displayName;

            safeField.readonly = parent?.field?.readonly
                ? parent?.field?.readonly
                : safeField.readonly;

            safeField.hidden = parent?.field?.hidden
                ? parent?.field?.hidden
                : safeField.hidden;

            safeField.displayName = name;

            const type = getMetadataEntityFieldType(safeField);
            const isArray = Array.isArray(field.type);

            switch (type) {
                case MetadataEntityFieldType.EmbeddedEntity: {
                    if (!isArray) {
                        this.getFields(getType, fieldMap, { path, field: safeField }, safeField.typeData?.fields, scope, groupName);
                    }
                    break;
                }

                case MetadataEntityFieldType.CustomField: {
                    path = `customFields.${safeField.name}`;
                    break;
                }

                // default: {
                //     fieldMap[path] = safeField;
                // }
            }

            safeField.path = path;
            safeField.scope = scope;
            const parentGroup = parent?.field?.displayName ? (groupName ? `${groupName} ${parent.field.displayName}` : parent.field.displayName) : groupName;
            safeField.group = parentGroup ?? null;
            safeField.qualifiedDisplayName = safeField.group ? `${safeField.group} · ${safeField.displayName}` : safeField.displayName;

            if (getType === 'customFields' && type !== MetadataEntityFieldType.CustomField) {
                path = null;
            }

            if (getType === 'bulkUpdate' && !isBulkUpdateEligible(safeField)) {
                path = null;
            }

            if (path)
                fieldMap[path] = safeField;


        }

        return fieldMap;
    }

    private mergeSecondaryFields(
        target: Record<string, MetadataEntityFieldData>,
        source: Record<string, MetadataEntityFieldData>,
        arrayPath: string,
        groupName?: string,
        scope: MetadataEntityRecordScope | string = MetadataEntityRecordScope.Secondary,
    ): void {

        for (const field of Object.values(source)) {
            const prefixedPath = `${arrayPath}.${field.path ?? field.name}`;
            const group = groupName ?? null;
            const qualifiedDisplayName = group ? `${group} · ${field.displayName}` : field.displayName;
            target[prefixedPath] = { ...field, path: prefixedPath, group, qualifiedDisplayName, scope };
        }
    }
}
